import React, { useState, useEffect } from 'react';

// --- Icons (Simulated Lucide React for a clean look) ---
const BriefcaseIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>;
const PlusIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>;
const EditIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5L2 22l1.5-5.5L17 3z"/></svg>;
const TrashIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>;
const SaveIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const ClockIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const AwardIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 22 12 18 17 22 15.79 13.88"/></svg>;
const XCircleIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
const UsersIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M19 21v-2a4 4 0 0 0-3-3.87"/><path d="M22 12A10 10 0 0 0 12 2"/></svg>;

// --- Constants & Initial Data ---
const PRIMARY_COLOR = 'indigo-600';
const SECONDARY_COLOR = 'purple-600';

const INITIAL_RULES = [
    { 
        id: 201, 
        name: 'Standard Tech Role Eligibility (L1/L2)', 
        minTenureMonths: 9, 
        minRating: 3.0, 
        noticePeriodStatus: 'NOT_UNDER_NOTICE', 
        managerApprovalRequired: true, 
        status: 'Active',
        departments: ['Technology', 'Product'],
        description: 'Requires 9 months tenure and an average rating of 3.0 or higher. Cannot be applied for while serving notice.',
    },
    { 
        id: 202, 
        name: 'Leadership/Management Track IJP', 
        minTenureMonths: 18, 
        minRating: 4.0, 
        noticePeriodStatus: 'ANY', 
        managerApprovalRequired: true, 
        status: 'Active',
        departments: ['Sales', 'Finance', 'Operations'],
        description: 'For management positions. High performance (4.0+) and longer tenure is required. Can apply even during notice period if approved by HR Head.',
    },
    { 
        id: 203, 
        name: 'Probationary Employee Exemption Rule', 
        minTenureMonths: 0, 
        minRating: 0.0, 
        noticePeriodStatus: 'NOT_UNDER_NOTICE', 
        managerApprovalRequired: false, 
        status: 'Inactive',
        departments: ['All'],
        description: 'Prevents any employee currently in their first review cycle (less than 6 months) from applying.',
    },
];

let nextId = 204;

/**
 * Settings Panel Component (Slide-out from Right)
 */
const SettingsPanel = ({ isOpen, onClose, rule, onSave }) => {
    const isNew = rule && !rule.id;
    
    // State for form data, initialized with rule props
    const [formData, setFormData] = useState(rule);

    useEffect(() => {
        if (rule) {
            setFormData({ 
                ...rule, 
                // Ensure number fields are correctly typed for inputs
                minTenureMonths: rule.minTenureMonths !== undefined ? rule.minTenureMonths : 6,
                minRating: rule.minRating !== undefined ? rule.minRating : 3.0,
                departments: rule.departments || [],
                status: rule.status || 'Active'
            });
        }
    }, [rule]);

    if (!isOpen || !formData) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'departmentsText') {
            // Handle comma-separated department list
            const departmentsArray = value.split(',').map(d => d.trim()).filter(d => d.length > 0);
            setFormData(prev => ({ ...prev, departmentsText: value, departments: departmentsArray }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' 
                    ? checked 
                    : (name === 'minTenureMonths' ? Number(value) : (name === 'minRating' ? parseFloat(value) : value)),
            }));
        }
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        
        const ruleToSave = {
            ...formData,
            name: formData.name.trim(),
            // Clean up the temporary text field and ensure correct types
            departmentsText: undefined,
            minTenureMonths: Number(formData.minTenureMonths),
            minRating: parseFloat(formData.minRating),
            status: formData.status === true || formData.status === 'Active' ? 'Active' : 'Inactive',
        };
        
        onSave(ruleToSave);
    };

    // Determine what to show in the department textarea
    const departmentListText = (formData.departmentsText !== undefined) 
        ? formData.departmentsText 
        : (formData.departments || []).join(', ');

    // Panel classes for slide animation
    const panelClasses = `bg-white w-full max-w-lg h-full shadow-2xl transform transition-transform duration-300 ease-out 
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
    `;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
            {/* Overlay */}
            <div 
                className={`absolute inset-0 bg-gray-900 transition-opacity duration-300 ${isOpen ? 'opacity-60' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            ></div>
            
            {/* Panel Content - Flex container for fixed header/footer and scrollable body */}
            <div 
                className={panelClasses}
                role="dialog"
                aria-modal="true"
                aria-labelledby="panel-title"
                tabIndex={-1}
            >
                <form onSubmit={handleSubmit} className="flex flex-col h-full">
                    
                    {/* Panel Header (Fixed) */}
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
                        <h3 id="panel-title" className="text-xl font-poppins font-semibold text-gray-800">
                            {isNew ? 'Define New IJP Rule' : `Editing: ${rule.name}`}
                        </h3>
                        <button 
                            type="button"
                            onClick={onClose} 
                            className="text-gray-400 hover:text-gray-600 transition p-1 rounded-full focus:ring-2 focus:ring-indigo-400"
                            aria-label="Close panel"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </div>
                    
                    {/* Panel Body: Advanced Form (SCROLLABLE AREA - flex-grow ensures it takes remaining height) */}
                    <div className="p-6 space-y-5 overflow-y-auto flex-grow">
                        {/* Rule Details */}
                        <div>
                            <label htmlFor="name" className="advanced-label">Rule Name *</label>
                            <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required className="advanced-input" placeholder="e.g., Standard Tech Role Eligibility" />
                        </div>
                        <div>
                            <label htmlFor="description" className="advanced-label">Rule Description (Internal Note)</label>
                            <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows="2" className="advanced-input" placeholder="Explain the purpose of this rule and the employees it targets." />
                        </div>

                        {/* Conditional Triggers */}
                        <p className="text-sm text-gray-500 border-b pb-3 pt-4 font-medium flex items-center">
                            <UsersIcon className="w-4 h-4 mr-2 text-indigo-500"/> Employee Eligibility Criteria
                        </p>
                        
                        <div className="grid grid-cols-2 gap-4">
                            {/* Min Tenure */}
                            <div>
                                <label htmlFor="minTenureMonths" className="advanced-label flex items-center"><ClockIcon className="w-3.5 h-3.5 mr-1" /> Min. Tenure (Months) *</label>
                                <input id="minTenureMonths" name="minTenureMonths" type="number" min="0" value={formData.minTenureMonths} onChange={handleChange} required className="advanced-input" />
                            </div>
                            
                            {/* Min Rating */}
                            <div>
                                <label htmlFor="minRating" className="advanced-label flex items-center"><AwardIcon className="w-3.5 h-3.5 mr-1" /> Min. Avg. Performance Rating (e.g., 3.0) *</label>
                                <input id="minRating" name="minRating" type="number" min="0" max="5" step="0.1" value={formData.minRating} onChange={handleChange} required className="advanced-input" />
                            </div>
                        </div>

                        {/* Notice Period Status */}
                        <div>
                            <label htmlFor="noticePeriodStatus" className="advanced-label flex items-center"><XCircleIcon className="w-3.5 h-3.5 mr-1" /> Notice Period Status Restriction *</label>
                            <select id="noticePeriodStatus" name="noticePeriodStatus" value={formData.noticePeriodStatus} onChange={handleChange} required className="advanced-input">
                                <option value="NOT_UNDER_NOTICE">Must NOT be serving Notice Period</option>
                                <option value="ANY">No Restriction (Allowed while serving Notice)</option>
                            </select>
                        </div>
                        
                        {/* Department Filters */}
                        <div>
                            <label htmlFor="departmentsText" className="advanced-label">Target Departments (Comma Separated)</label>
                            <textarea
                                id="departmentsText"
                                name="departmentsText"
                                value={departmentListText}
                                onChange={handleChange}
                                rows="2"
                                className="advanced-input"
                                placeholder="e.g., Technology, Product, HR. Use 'All' for no department filter."
                            />
                        </div>

                        {/* Approvals and Status */}
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                            <label htmlFor="managerApprovalRequired" className="advanced-label flex items-center cursor-pointer space-x-2">
                                <input
                                    id="managerApprovalRequired"
                                    name="managerApprovalRequired"
                                    type="checkbox"
                                    checked={formData.managerApprovalRequired}
                                    onChange={(e) => handleChange({ target: { name: 'managerApprovalRequired', type: 'checkbox', checked: e.target.checked } })}
                                    className={`w-4 h-4 text-${PRIMARY_COLOR} border-gray-300 rounded focus:ring-${PRIMARY_COLOR}`}
                                />
                                <span>Require Manager Approval</span>
                            </label>
                            
                            <label htmlFor="status" className="advanced-label flex items-center cursor-pointer space-x-2">
                                <input
                                    id="status"
                                    name="status"
                                    type="checkbox"
                                    checked={formData.status === 'Active'}
                                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'Active' : 'Inactive' }))}
                                    className={`w-4 h-4 text-green-500 border-gray-300 rounded focus:ring-green-500`}
                                />
                                <span>Rule is **Active**</span>
                            </label>
                        </div>
                    </div>
                    
                    {/* Panel Footer (Fixed) */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3 flex-shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2 text-sm font-roboto font-medium rounded-lg text-gray-700 hover:bg-gray-100 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-5 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md flex items-center space-x-1 transition 
                                bg-${SECONDARY_COLOR} hover:bg-purple-700 disabled:bg-gray-400
                            `}
                            disabled={!formData.name.trim() || formData.minTenureMonths < 0 || formData.minRating < 0}
                        >
                            <SaveIcon className="w-4 h-4" />
                            <span>{isNew ? 'Create Rule' : 'Save Rule'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


/**
 * Individual Rule Card Component (Main List)
 */
const SettingsCard = ({ rule, onEdit, onDelete }) => {
    const statusColor = rule.status === 'Active' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    const approvalText = rule.managerApprovalRequired ? 'Manager Approval Required' : 'No Approval Needed';
    
    const tenureText = rule.minTenureMonths === 0 
        ? 'No Minimum Tenure' 
        : `${rule.minTenureMonths} months Min. Tenure`;
    
    const ratingText = rule.minRating === 0
        ? 'No Min. Rating'
        : `${rule.minRating.toFixed(1)} Min. Avg. Rating`;

    const noticeText = rule.noticePeriodStatus === 'ANY'
        ? 'Notice Period: Allowed'
        : 'Notice Period: Restricted';

    const departmentText = rule.departments.length === 0 || rule.departments.includes('All') 
        ? 'All Departments' 
        : `${rule.departments.length} Depts.`;

    return (
        <div
            className={`p-4 rounded-xl cursor-pointer transition duration-150 round-card-focus bg-white hover:bg-gray-50 border border-gray-200 shadow-sm`}
            tabIndex={0} 
            role="button"
            onClick={() => onEdit(rule)} // Clicking anywhere opens the panel
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onEdit(rule);
                }
            }}
        >
            <div className="flex justify-between items-start">
                {/* Left: Details */}
                <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center space-x-3">
                        <span className={`text-lg font-poppins font-semibold truncate text-gray-800`}>
                            {rule.name}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${statusColor}`}>
                            {rule.status}
                        </span>
                    </div>

                    {/* Criteria */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs mt-2 text-gray-700 font-medium">
                        <span className="flex items-center text-indigo-700"><ClockIcon className="w-3.5 h-3.5 mr-1" /> {tenureText}</span>
                        <span className="flex items-center text-purple-700"><AwardIcon className="w-3.5 h-3.5 mr-1" /> {ratingText}</span>
                        <span className="flex items-center text-red-700"><XCircleIcon className="w-3.5 h-3.5 mr-1" /> {noticeText}</span>
                        <span className="flex items-center text-green-700"><UsersIcon className="w-3.5 h-3.5 mr-1" /> {departmentText}</span>
                    </div>
                    
                    {/* Description/Approvals */}
                    <p className={`text-sm mt-2 font-medium text-gray-700 border-t border-gray-100 pt-2`}>
                        <span className="font-semibold text-gray-800">Approvals:</span> {approvalText}
                    </p>
                </div>

                {/* Right: Actions */}
                <div className="flex space-x-2 flex-shrink-0 pt-1">
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(rule); }} // Edit button opens panel
                        className="p-1.5 text-gray-500 hover:text-purple-600 rounded-full hover:bg-purple-100 transition focus:ring-2 focus:ring-purple-400"
                        aria-label={`Edit ${rule.name}`}
                    >
                        <EditIcon />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(rule.id); }}
                        className="p-1.5 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-100 transition focus:ring-2 focus:ring-red-400"
                        aria-label={`Delete ${rule.name}`}
                    >
                        <TrashIcon />
                    </button>
                </div>
            </div>
        </div>
    );
};


/**
 * Main Internal Job Posting (IJP) Settings Component
 */
const InternalJobsSettings = () => {
    const [rules, setRules] = useState(INITIAL_RULES);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [currentRule, setCurrentRule] = useState(null);
    
    const handleSaveRule = (savedRule) => {
        if (savedRule.id) {
            // Update existing rule
            setRules(rules.map(r => r.id === savedRule.id ? savedRule : r));
        } else {
            // Add new rule
            const newRule = { ...savedRule, id: nextId++ };
            setRules([...rules, newRule]);
        }
        setCurrentRule(null);
        setIsPanelOpen(false);
    };

    const handleNewRuleClick = () => {
        setCurrentRule({ 
            id: null, 
            name: '', 
            minTenureMonths: 6, 
            minRating: 3.0, 
            noticePeriodStatus: 'NOT_UNDER_NOTICE', 
            managerApprovalRequired: true, 
            status: 'Active', 
            departments: [],
            description: '' 
        });
        setIsPanelOpen(true);
    };

    const handleEditRule = (rule) => {
        setCurrentRule(rule);
        setIsPanelOpen(true);
    };

    const handleDeleteRule = (id) => {
        if (window.confirm('Are you sure you want to delete this Internal Job Posting eligibility rule?')) {
            setRules(rules.filter(r => r.id !== id));
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-6 sm:p-8 font-roboto">
            {/* Global Styles for HRMS Look and Focus Fix */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@400;500;700&display=swap');
                    .font-poppins { font-family: 'Poppins', sans-serif; }
                    .font-roboto { font-family: 'Roboto', sans-serif; }
                    /* Custom colors matching the visual style */
                    .bg-indigo-600 { background-color: #4f46e5; }
                    .hover\\:bg-indigo-700:hover { background-color: #4338ca; }
                    .text-indigo-600 { color: #4f46e5; }
                    .ring-indigo-600 { --tw-ring-color: #4f46e5; }

                    .bg-purple-600 { background-color: #9333ea; }
                    .hover\\:bg-purple-700:hover { background-color: #7e22ce; }
                    .text-purple-600 { color: #9333ea; }

                    /* --- GLOBAL FOCUS FIX (Fixing the unwanted blue glow) --- */
                    *:focus { outline: none !important; }
                    
                    /* Applies custom, styled focus ring only when keyboard navigation is used */
                    .advanced-input:focus-visible,
                    .round-card-focus:focus-visible,
                    button:focus-visible,
                    select:focus-visible {
                        outline: 2px solid transparent;
                        outline-offset: 2px;
                        border-color: #6366f1; /* indigo-500 */
                        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.4) !important; /* Custom focus ring */
                    }
                    
                    /* Advanced Input/Label Styles */
                    .advanced-label {
                        display: block;
                        font-size: 0.875rem; /* text-sm */
                        font-weight: 500; /* font-medium */
                        color: #374151; /* text-gray-700 */
                        margin-bottom: 0.25rem;
                        font-family: 'Roboto', sans-serif;
                    }
                    .advanced-input {
                        width: 100%;
                        padding: 0.65rem 0.75rem;
                        border: 1px solid #d1d5db;
                        border-radius: 0.5rem;
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                        font-size: 0.875rem;
                        font-family: 'Roboto', sans-serif;
                        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                        -webkit-appearance: none;
                        -moz-appearance: none;
                        appearance: none;
                    }
                `}
            </style>

            <div className="max-w-4xl w-full bg-white rounded-xl shadow-2xl border border-gray-100 mt-4 overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-white flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-poppins font-semibold text-gray-800 flex items-center">
                            <BriefcaseIcon className={`w-7 h-7 mr-3 text-indigo-600`} />
                            Internal Job Posting (IJP) Settings
                        </h1>
                        <p className="text-sm text-gray-500 mt-1 font-roboto">
                            Configure eligibility rules to control who can apply for internal job openings.
                        </p>
                    </div>
                    <button
                        onClick={handleNewRuleClick}
                        className={`px-4 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md bg-green-500 hover:bg-green-600 flex items-center space-x-1 transition`}
                    >
                        <PlusIcon className="w-4 h-4" />
                        <span>New Eligibility Rule</span>
                    </button>
                </div>

                {/* Main Content: Rules List */}
                <div className="p-6 space-y-4">
                    <div className="flex justify-between items-center text-gray-600 text-sm font-medium border-b pb-2">
                        <h2 className="text-lg font-poppins font-medium text-gray-700">Defined Rules ({rules.length})</h2>
                        <span className="text-xs text-gray-500">IJP process checks these rules sequentially.</span>
                    </div>

                    {rules.length > 0 ? (
                        <div className="space-y-3">
                            {rules.map((rule) => (
                                <SettingsCard
                                    key={rule.id}
                                    rule={rule}
                                    onEdit={handleEditRule}
                                    onDelete={handleDeleteRule}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center p-12 bg-white border border-dashed border-gray-200 rounded-xl">
                            <BriefcaseIcon className="w-8 h-8 mx-auto text-red-500 mb-3" />
                            <p className="text-md font-poppins text-gray-700 mb-1 font-semibold">
                                No IJP Rules Found
                            </p>
                            <p className="text-sm text-gray-500 font-roboto">
                                All employees will be eligible by default. Click '+ New Eligibility Rule' to enforce restrictions.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Rule Modal Component - Now a Side Panel with Fixed Header/Footer */}
            <SettingsPanel
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                rule={currentRule}
                onSave={handleSaveRule}
            />
        </div>
    );
};

export default InternalJobsSettings;